import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';

import { unwrap } from '@/api/envelope';
import { staffOrganizationActivity, type OrganizationResponse } from '@/api/generated';
import { ROUTES } from '@/app/routes';
import { Button } from '@/components/ui/Button';

import { ActivityPanel } from './ActivityPanel';
import { ORGANIZATION_PARAM } from './StaffActivityPage';
import { toActivityEntries, toFeedEntries } from './accountActivity';

/** How many entries the panel shows; the rest are on the Activity page. */
const PANEL_SIZE = 10;

/**
 * The account page's Recent activity: the latest entries from this account's
 * audit feed, and a way through to all of them.
 *
 * Its own query and its own failure state, as the Super Admins card has, so a
 * feed outage leaves the rest of the account readable. An account older than
 * the log has an empty feed; for that one the panel shows the dates the record
 * itself holds, as it always did, and claims nobody did them.
 */
export function RecentActivity({ record }: { record: OrganizationResponse }) {
  const feed = useQuery({
    queryKey: ['staff', 'organizations', record.id, 'activity'],
    queryFn: async () => {
      const reply = await staffOrganizationActivity({
        path: { organization_id: record.id },
        query: { page: 1, limit: PANEL_SIZE },
        throwOnError: true,
      });
      return unwrap(reply.data)?.items ?? [];
    },
  });

  if (feed.isPending) {
    return <Typography role="status">Loading activity…</Typography>;
  }

  if (feed.isError) {
    return (
      <Alert
        severity="error"
        role="alert"
        action={
          <Button size="sm" variant="ghost" onClick={() => void feed.refetch()}>
            Try again
          </Button>
        }
      >
        This account’s activity could not be loaded.
      </Alert>
    );
  }

  const fromFeed = feed.data.length > 0;

  return (
    <ActivityPanel
      embedded
      entries={fromFeed ? toFeedEntries(feed.data) : toActivityEntries(record)}
      footer={
        <Link
          component={RouterLink}
          to={`${ROUTES.staff.activity}?${ORGANIZATION_PARAM}=${record.id}`}
          variant="body2"
        >
          View all activity
        </Link>
      }
    />
  );
}
